import { groupBy, sumBy } from 'lodash-es'
import pluralize from 'pluralize'
import { useMemo } from 'react'

import { formatNumber, totalShipmentWeight } from '@fv/client-core'
import type { FullShipment } from '@fv/client-types'
import type { LoadItem } from '@fv/models'
import type { DTO } from '@fv/models/core'

import { useTypeNames } from '@/hooks/settings'

type Props = {
  load: Pick<FullShipment, 'items' | 'equipment'>
  handlingUnitsLabel?: string
  containsLabel?: string
  emptyLabel?: string
}

export const ShipmentItemTypeSummary = ({
  load,
  handlingUnitsLabel = '',
  containsLabel = 'Containing',
  emptyLabel = 'No item details provided',
}: Props) => {
  const { items, equipment } = load
  const { handlingUnitsSummary, containsSummary } =
    useShipmentItemTypeCountSummary(items)
  const weight = totalShipmentWeight(load)
  const summary = [
    handlingUnitsSummary
      ? `${handlingUnitsLabel} ${handlingUnitsSummary}`.trim()
      : null,
    containsSummary ? `${containsLabel} ${containsSummary}`.trim() : null,
    weight ? `${formatNumber(weight)} ${equipment.weightUOM}` : null,
  ]
    .filter(Boolean)
    .join(' / ')

  return <span>{summary || emptyLabel}</span>
}

type ItemCount = {
  count: number
  label: string
}

const toCount = (quantity: number | string | undefined) => {
  const count = Number(quantity)
  return Number.isFinite(count) && count > 0 ? count : 0
}

const summarizeCounts = (items: ItemCount[]) =>
  Object.entries(groupBy(items, 'label')).map(([label, grouped]) => ({
    count: sumBy(grouped, 'count'),
    label,
  }))

const formatTypeCounts = (countsByType: ItemCount[]) =>
  countsByType
    .map(
      ({ label, count }) => `${formatNumber(count)} ${pluralize(label, count)}`,
    )
    .join(', ')

const useShipmentItemTypeCountSummary = (items: DTO<LoadItem>[]) => {
  const { packageName } = useTypeNames()

  return useMemo(() => {
    const handlingUnitCounts = summarizeCounts(
      items
        .map(item => ({
          count: toCount(item.quantity),
          label: item.type ? packageName(item.type) : 'unit',
        }))
        .filter(item => item.count > 0),
    )
    const containsCounts = summarizeCounts(
      items
        .flatMap(item => item.contains ?? [])
        .map(item => ({
          count: toCount(item.quantity),
          label: item.type ? packageName(item.type) : 'unit',
        }))
        .filter(item => item.count > 0),
    )

    return {
      handlingUnitsSummary: formatTypeCounts(handlingUnitCounts),
      containsSummary: formatTypeCounts(containsCounts),
    }
  }, [items, packageName])
}
